import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase, isSupabaseConfigured } from '../config/supabaseClient'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [processingAuth, setProcessingAuth] = useState(false)

  // Verificar si Supabase està configurat
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      console.warn('⚠️ Supabase no está configurado. El sistema de autenticación no funcionará.')
      setLoading(false)
      return
    }

    let isMounted = true

    const initAuth = async () => {
      await checkUser()
    }

    initAuth()

    // Escoltar canvis en l'autenticació
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return

      // Evitar procesar si ya se está procesando
      if (processingAuth) {
        console.log('⏳ Ya se está procesando autenticación, saltando...')
        return
      }

      console.log('Auth event:', event)

      if (session?.user) {
        setProcessingAuth(true)
        console.log('👤 Actualizando estado user:', session.user.email)
        setUser(session.user)

        // Verificar/crear usuari a Usuaris i obtenir rol
        try {
          await ensureUserInUsuaris(session.user)
          await fetchUserRole(session.user.id)
        } catch (error) {
          console.error('Error en procesamiento de autenticación:', error)
        } finally {
          setProcessingAuth(false)
        }
      } else {
        setUser(null)
        setUserRole(null)
        setProcessingAuth(false)
      }
      setLoading(false)
    })

    return () => {
      isMounted = false
      authListener?.subscription?.unsubscribe()
    }
  }, [])

  // Verificar usuari actual
  const checkUser = async () => {
    if (!isSupabaseConfigured()) return

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (session?.user) {
        setUser(session.user)
        // No llamar a ensureUserInUsuaris y fetchUserRole aquí
        // Dejar que onAuthStateChange lo maneje
      }
    } catch (error) {
      console.error('Error checking user:', error)
    } finally {
      setLoading(false)
    }
  }

  // Assegurar que l'usuari existeix a la taula Usuaris
  const ensureUserInUsuaris = async user => {
    if (!isSupabaseConfigured()) return

    try {
      console.log('🔍 Verificando usuario en tabla Usuaris...')

      // Verificar si l'usuari existeix
      const { data: usuariData, error: usuariError } = await supabase.from('Usuaris').select('*').eq('auth_user_id', user.id).maybeSingle() // Usar maybeSingle en lugar de single para evitar errores

      if (usuariError && usuariError.code !== 'PGRST116') {
        // Error diferent a "no trobat"
        console.error('❌ Error consultando Usuaris:', usuariError)
        return
      }

      if (!usuariData) {
        console.warn('⚠️ Usuario no encontrado en Usuaris, creando...')

        // Crear l'usuari a Usuaris
        const { error: insertError } = await supabase.from('Usuaris').insert([
          {
            auth_user_id: user.id,
            nom: user.user_metadata?.name || user.email.split('@')[0],
            cognom: 'Usuario',
            email: user.email,
            rol: 'user',
          },
        ])

        if (insertError) {
          console.error('❌ Error creando usuario en Usuaris:', insertError)
        } else {
          console.log('✅ Usuario creado en Usuaris')
        }
      } else {
        console.log('✅ Usuario encontrado en Usuaris:', usuariData.nom)
      }
    } catch (error) {
      console.error('❌ Error en ensureUserInUsuaris:', error)
    }
  }

  // Obtenir rol de l'usuari des de la base de dades
  const fetchUserRole = async userId => {
    if (!isSupabaseConfigured()) return

    try {
      console.log('🔍 Obteniendo rol para usuario:', userId)
      const { data, error } = await supabase.from('Usuaris').select('rol').eq('auth_user_id', userId).maybeSingle() // Usar maybeSingle en lugar de single

      if (error) {
        console.error('❌ Error obteniendo rol:', error)
        setUserRole('user')
        return
      }

      console.log('✅ Rol obtenido:', data?.rol)
      setUserRole(data?.rol || 'user')
    } catch (error) {
      console.error('❌ Error en fetchUserRole:', error)
      setUserRole('user')
    }
  }

  // Registrar nou usuari
  const signUp = async (email, password, userData) => {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase no está configurado' } }
    }

    try {
      console.log('📝 Registrando usuario:', email)
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
          },
          emailRedirectTo: window.location.hostname.includes('pages.dev') 
            ? 'https://motorvault.pages.dev' 
            : 'http://localhost:3000',
        },
      })

      if (error) {
        console.error('❌ Error en registro:', error)
        throw error
      }

      console.log('✅ Usuario registrado en auth.users:', data.user?.email)

      // NO intentar crear en Usuaris aquí
      // El trigger de la base de datos lo hará automáticamente
      // O lo hará el onAuthStateChange cuando se confirme el email

      return { data, error: null }
    } catch (error) {
      console.error('❌ Error en signUp:', error)
      
      // Traducir mensajes de error al español
      let errorMessage = error.message
      if (error.message.includes('already registered')) {
        errorMessage = 'Este email ya está registrado. Por favor, inicia sesión.'
      } else if (error.message.includes('Password should be at least')) {
        errorMessage = 'La contraseña debe tener al menos 6 caracteres.'
      } else if (error.message.includes('Invalid email')) {
        errorMessage = 'El formato del email no es válido.'
      } else if (error.message.includes('row-level security')) {
        errorMessage = 'Error de configuración. Por favor, contacta al administrador.'
      }

      return { data: null, error: { ...error, message: errorMessage } }
    }
  }

  // Iniciar sesión
  const signIn = async (email, password, shouldSignOutFirst = false) => {
    if (!isSupabaseConfigured()) {
      return { data: null, error: { message: 'Supabase no está configurado' } }
    }

    try {
      // Si se sol·licita, tancar sessió primer per netejar l'estat
      if (shouldSignOutFirst) {
        console.log('🔄 Cerrando sesión previa...')
        await supabase.auth.signOut()
        await new Promise(resolve => setTimeout(resolve, 500)) // Esperar que l'esdeveniment es processi
        return { data: null, error: null }
      }

      console.log('🔑 Intentando iniciar sesión con Supabase...')

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      console.log('📥 Respuesta recibida de Supabase')

      if (error) {
        console.error('❌ Error de autenticación:', error.message)

        // Traducir mensaje de error al español
        let errorMessage = error.message
        if (error.message === 'Invalid login credentials') {
          errorMessage = 'Email o contraseña incorrectos. Por favor, verifica tus credenciales.'
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Por favor, confirma tu email antes de iniciar sesión.'
        } else if (error.message.includes('User not found')) {
          errorMessage = 'No existe una cuenta con este email.'
        }

        return { data: null, error: { ...error, message: errorMessage } }
      }

      console.log('✅ Supabase confirmó las credenciales correctas')
      console.log('✅ Usuario autenticado:', data.user?.email)

      return { data, error: null }
    } catch (error) {
      console.error('❌ Error inesperado en signIn:', error)
      return { data: null, error: { message: error.message || 'Error inesperado' } }
    }
  }

  // Tancar sessió
  const signOut = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('⚠️ Supabase no configurado, no se puede cerrar sesión')
      return { error: null }
    }

    try {
      console.log('🚪 Cerrando sesión...')

      // Limpiar estado inmediatamente
      setUser(null)
      setUserRole(null)

      // Llamar a signOut con un timeout para no quedarse colgado
      console.log('📡 Llamando a supabase.auth.signOut() con timeout...')

      const signOutPromise = supabase.auth.signOut()
      const timeoutPromise = new Promise(resolve => setTimeout(() => resolve({ timeout: true }), 2000))

      const result = await Promise.race([signOutPromise, timeoutPromise])

      if (result.timeout) {
        console.log('⏱️ Timeout - limpiando localStorage manualmente')
        // Limpiar todas las claves de Supabase manualmente
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-')) {
            localStorage.removeItem(key)
          }
        })
      }

      console.log('✅ Sesión cerrada y localStorage limpiado')
      return { error: null }
    } catch (error) {
      console.error('❌ Error CAPTURADO en signOut:', error)
      // Forzar limpieza manual del localStorage si falla
      try {
        // Limpiar todas las claves de Supabase
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-')) {
            localStorage.removeItem(key)
          }
        })
        console.log('🧹 localStorage limpiado manualmente')
      } catch (e) {
        console.error('Error limpiando localStorage:', e)
      }
      return { error }
    }
  }

  // Recuperar contrasenya
  const resetPassword = async email => {
    if (!isSupabaseConfigured()) {
      return { error: { message: 'Supabase no está configurado' } }
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  // Actualitzar contrasenya
  const updatePassword = async newPassword => {
    if (!isSupabaseConfigured()) {
      return { error: { message: 'Supabase no está configurado' } }
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  // Verificar si és admin
  const isAdmin = () => {
    return userRole === 'admin'
  }

  const value = {
    user,
    userRole,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    isAdmin,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
